import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Pause,
  Play,
  Archive,
  Bell,
  MoreHorizontal,
  DollarSign,
  Eye,
  MousePointerClick,
  Target,
  Calendar,
  MapPin,
  Users,
  Heart,
  Hash,
  Activity,
  ImageIcon,
  Pencil,
  Copy,
  Clock,
  Ticket,
  Send,
  Rocket,
  RefreshCw,
  AlertTriangle,
  CreditCard,
} from 'lucide-react';

import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppTabs } from '@/shared/components/ui/AppTabs';
import { AppCard } from '@/shared/components/ui/AppCard';
import { AppDropdownMenu } from '@/shared/components/ui/AppDropdownMenu';
import { AppDialog } from '@/shared/components/ui/AppDialog';
import { CampaignStatusBadge } from '@/shared/components/promote/CampaignStatusBadge';
import { MetricCard } from '@/shared/components/promote/MetricCard';
import { PlatformTag } from '@/shared/components/promote/PlatformTag';
import { NotificationList } from '@/shared/components/promote/NotificationList';
import { AppBadge } from '@/shared/components/ui/AppBadge';
import { CampaignStatus } from '@/shared/types/enums';
import type { AdPlatform } from '@/shared/types/enums';
import type {
  CampaignCreative,
  CampaignMetrics,
  DailyMetric,
  Notification,
} from '@/shared/types/entities';
import { useNotifications, useMarkNotificationRead } from '@/features/notifications';
import { ROUTES } from '@/app/routes';

import { useCampaign, useCampaignMetrics, useAssetAccessUrl } from '../../api/campaigns-queries';
import { CreativeEditDialog } from '../../components/CreativeEditDialog';
import { getTargetingCapabilities } from '../../logic/platform-constraints';
import { checkoutEnabled } from '../../logic/stripe';
import {
  usePauseCampaign,
  useResumeCampaign,
  useArchiveCampaign,
  useDuplicateCampaign,
  useSubmitForReview,
  useSubmitCampaign,
  useSyncCampaignStatus,
} from '../../api/campaigns-mutations';
import styles from './CampaignDetailPage.module.scss';

function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

function formatPercent(n: number): string {
  return `${n.toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

function formatChartDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateStr));
}

function prepareChartData(dailyMetrics: DailyMetric[]) {
  return dailyMetrics.map((m) => ({
    date: formatChartDate(m.date),
    impressions: m.impressions,
    clicks: m.clicks,
    spend: m.spendCents / 100,
    conversions: m.conversions,
    ctr: m.clicks > 0 && m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0,
  }));
}

function computeDaysInfo(startDate: string, endDate?: string) {
  const now = new Date();
  const start = new Date(startDate);
  const msPerDay = 1000 * 60 * 60 * 24;
  const elapsed = Math.max(0, Math.floor((now.getTime() - start.getTime()) / msPerDay));

  if (!endDate) {
    return { elapsed, remaining: null, totalDays: null };
  }

  const end = new Date(endDate);
  const totalDays = Math.max(1, Math.floor((end.getTime() - start.getTime()) / msPerDay));
  const remaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / msPerDay));
  return { elapsed, remaining, totalDays };
}

interface OverviewTabProps {
  campaign: {
    id: string;
    name: string;
    objective: string;
    budgetCents: number;
    spentCents: number;
    budgetType: string;
    currency: string;
    startDate: string;
    endDate?: string;
    platform: string;
    eventName?: string;
  };
}

function OverviewTab({ campaign }: OverviewTabProps) {
  const budgetPercent =
    campaign.budgetCents > 0
      ? Math.min(100, Math.round((campaign.spentCents / campaign.budgetCents) * 100))
      : 0;

  const daysInfo = useMemo(
    () => computeDaysInfo(campaign.startDate, campaign.endDate),
    [campaign.startDate, campaign.endDate],
  );

  return (
    <motion.div
      className={styles.overviewContainer}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <div className={styles.overviewGrid}>
        <AppCard className={styles.infoCard}>
          <h4 className={styles.infoLabel}>Objective</h4>
          <p className={styles.infoValue}>
            {campaign.objective
              .replace('_', ' ')
              .toLowerCase()
              .replace(/^\w/, (c) => c.toUpperCase())}
          </p>
        </AppCard>
        <AppCard className={styles.infoCard}>
          <h4 className={styles.infoLabel}>Budget</h4>
          <p className={styles.infoValue}>
            {formatCurrency(campaign.budgetCents, campaign.currency)}
            <span className={styles.budgetType}> / {campaign.budgetType}</span>
          </p>
          <div className={styles.progressBarWrapper}>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${budgetPercent}%` }} />
            </div>
            <span className={styles.progressLabel}>
              {formatCurrency(campaign.spentCents, campaign.currency)} spent ({budgetPercent}%)
            </span>
          </div>
        </AppCard>
        <AppCard className={styles.infoCard}>
          <h4 className={styles.infoLabel}>Date Range</h4>
          <p className={styles.infoValue}>
            <Calendar size={14} />
            {formatDate(campaign.startDate)}
            {campaign.endDate && ` - ${formatDate(campaign.endDate)}`}
          </p>
          <div className={styles.daysInfo}>
            <Clock size={12} />
            <span>
              {daysInfo.elapsed} day{daysInfo.elapsed !== 1 ? 's' : ''} elapsed
              {daysInfo.remaining !== null && (
                <>
                  {' '}
                  &middot; {daysInfo.remaining} day{daysInfo.remaining !== 1 ? 's' : ''} remaining
                </>
              )}
            </span>
          </div>
        </AppCard>
        {campaign.eventName && (
          <AppCard className={styles.infoCard}>
            <h4 className={styles.infoLabel}>
              <Ticket size={14} />
              Linked Event
            </h4>
            <p className={styles.infoValue}>{campaign.eventName}</p>
          </AppCard>
        )}
      </div>

      <MetricsSection campaignId={campaign.id} currency={campaign.currency} />
    </motion.div>
  );
}

interface CampaignPerformanceProps {
  metrics: CampaignMetrics;
  currency: string;
}

function CampaignPerformance({ metrics, currency }: CampaignPerformanceProps) {
  const chartData = useMemo(() => prepareChartData(metrics.dailyMetrics), [metrics.dailyMetrics]);

  return (
    <div className={styles.metricsContainer}>
      <h3 className={styles.sectionTitle}>Performance</h3>
      <div className={styles.metricsGrid}>
        <MetricCard
          label="Impressions"
          value={formatNumber(metrics.impressions)}
          icon={<Eye size={18} />}
        />
        <MetricCard
          label="Clicks"
          value={formatNumber(metrics.clicks)}
          icon={<MousePointerClick size={18} />}
        />
        <MetricCard
          label="Total Spend"
          value={formatCurrency(metrics.spendCents, currency)}
          icon={<DollarSign size={18} />}
        />
        {/* Backend returns CTR as a fraction (0-1); formatPercent expects 0-100. */}
        <MetricCard
          label="CTR"
          value={formatPercent(metrics.ctr * 100)}
          icon={<Target size={18} />}
        />
      </div>

      <AppCard className={styles.chartCard}>
        <h4 className={styles.chartTitle}>Impressions & Clicks Over Time</h4>
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: 13,
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="impressions"
                stroke="var(--color-primary)"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="clicks"
                stroke="var(--color-secondary)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </AppCard>

      <AppCard className={styles.chartCard}>
        <h4 className={styles.chartTitle}>Daily Spend</h4>
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }} />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: 13,
                }}
                formatter={(value: number) => [formatCurrency(value * 100), 'Spend']}
              />
              <Bar dataKey="spend" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </AppCard>
    </div>
  );
}

interface TargetingTabProps {
  targetingConfig: Record<string, unknown>;
  platform?: AdPlatform;
}

function TargetingTab({ targetingConfig, platform }: TargetingTabProps) {
  const targetingCaps = getTargetingCapabilities(platform);
  const locations = (targetingConfig.locations ?? []) as { name: string; type: string }[];
  const ageMin = (targetingConfig.ageMin as number) ?? null;
  const ageMax = (targetingConfig.ageMax as number) ?? null;
  const genders = (targetingConfig.genders ?? []) as string[];
  const interests = (targetingConfig.interests ?? []) as { id: string; name: string }[];
  const behaviors = (targetingConfig.behaviors ?? []) as { id: string; name: string }[];
  const keywords = targetingCaps.keywords ? ((targetingConfig.keywords ?? []) as string[]) : [];

  const hasData =
    locations.length > 0 ||
    ageMin !== null ||
    genders.length > 0 ||
    interests.length > 0 ||
    behaviors.length > 0 ||
    keywords.length > 0;

  if (!hasData) {
    return (
      <div className={styles.tabPlaceholder}>
        <p>No targeting configured for this campaign.</p>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.targetingGrid}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      {locations.length > 0 && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <MapPin size={14} />
            Locations
          </h4>
          <div className={styles.tagList}>
            {locations.map((loc, i) => (
              <AppBadge key={i} variant="info" size="sm">
                {loc.name}
                {loc.type && <span className={styles.tagMeta}> ({loc.type})</span>}
              </AppBadge>
            ))}
          </div>
        </AppCard>
      )}

      {(ageMin !== null || ageMax !== null) && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <Users size={14} />
            Age Range
          </h4>
          <p className={styles.infoValue}>
            {ageMin ?? 18} – {ageMax ?? 65}
          </p>
        </AppCard>
      )}

      {genders.length > 0 && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <Users size={14} />
            Genders
          </h4>
          <div className={styles.tagList}>
            {genders.map((g) => (
              <AppBadge key={g} variant="neutral" size="sm">
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </AppBadge>
            ))}
          </div>
        </AppCard>
      )}

      {interests.length > 0 && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <Heart size={14} />
            Interests
          </h4>
          <div className={styles.tagList}>
            {interests.map((interest) => (
              <AppBadge key={interest.id} variant="default" size="sm">
                {interest.name}
              </AppBadge>
            ))}
          </div>
        </AppCard>
      )}

      {behaviors.length > 0 && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <Activity size={14} />
            Behaviors
          </h4>
          <div className={styles.tagList}>
            {behaviors.map((behavior) => (
              <AppBadge key={behavior.id} variant="default" size="sm">
                {behavior.name}
              </AppBadge>
            ))}
          </div>
        </AppCard>
      )}

      {keywords.length > 0 && (
        <AppCard className={styles.targetingSection}>
          <h4 className={styles.infoLabel}>
            <Hash size={14} />
            Keywords
          </h4>
          <div className={styles.tagList}>
            {keywords.map((kw) => (
              <AppBadge key={kw} variant="neutral" size="sm">
                {kw}
              </AppBadge>
            ))}
          </div>
        </AppCard>
      )}
    </motion.div>
  );
}

const CREATIVE_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  draft: 'neutral',
  paused: 'warning',
  rejected: 'error',
};

// Google and Meta only accept creative changes while nothing is serving, which is
// also what the backend enforces on the update endpoint.
const CREATIVE_EDITABLE_STATUSES: CampaignStatus[] = [CampaignStatus.DRAFT, CampaignStatus.PAUSED];

interface CreativesTabProps {
  campaignId: string;
  platform: AdPlatform;
  status: CampaignStatus;
  creatives?: CampaignCreative[];
}

function CreativesTab({ campaignId, platform, status, creatives }: CreativesTabProps) {
  const [editingCreative, setEditingCreative] = useState<CampaignCreative | null>(null);
  const canEdit = CREATIVE_EDITABLE_STATUSES.includes(status);

  if (!creatives || creatives.length === 0) {
    return (
      <div className={styles.tabPlaceholder}>
        <p>No creatives yet for this campaign.</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        className={styles.creativesGrid}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {creatives.map((creative) => (
          <CreativeCard
            key={creative.id}
            creative={creative}
            canEdit={canEdit}
            onEdit={() => setEditingCreative(creative)}
          />
        ))}
      </motion.div>

      <CreativeEditDialog
        campaignId={campaignId}
        platform={platform}
        campaignStatus={status}
        creative={editingCreative}
        onClose={() => setEditingCreative(null)}
      />
    </>
  );
}

interface CreativeCardProps {
  creative: CampaignCreative;
  canEdit: boolean;
  onEdit: () => void;
}

function CreativeCard({ creative, canEdit, onEdit }: CreativeCardProps) {
  // Carousel creatives don't carry a direct media URL — their cards store
  // private storage keys, so resolve the first card's key to a signed URL
  // for the thumbnail.
  const firstCardKey = creative.mediaUrl ? undefined : creative.carouselCards?.[0]?.imageStorageKey;
  const { data: cardAsset } = useAssetAccessUrl(firstCardKey);
  const mediaUrl = creative.mediaUrl ?? cardAsset?.url;
  const cardCount = creative.carouselCards?.length ?? 0;
  // Image pool: the platform mixes every entry, so show them all.
  const poolUrls = creative.mediaUrls?.length ? creative.mediaUrls : mediaUrl ? [mediaUrl] : [];

  return (
    <AppCard className={styles.creativeCard}>
      {poolUrls.length > 1 ? (
        <div className={styles.creativeThumbnailGrid}>
          {poolUrls.map((url, i) => (
            <img
              key={`${url}-${i}`}
              src={url}
              alt={`${creative.headline || 'Creative asset'} (${i + 1})`}
              className={styles.creativeThumbnailImage}
            />
          ))}
        </div>
      ) : (
        <div className={styles.creativeThumbnail}>
          {poolUrls[0] ? (
            <img
              src={poolUrls[0]}
              alt={creative.headline || 'Creative asset'}
              className={styles.creativeThumbnailImage}
            />
          ) : (
            <div className={styles.creativePlaceholder}>
              <ImageIcon size={32} />
              <span>No media</span>
            </div>
          )}
        </div>
      )}
      <div className={styles.creativeContent}>
        <div className={styles.creativeHeader}>
          <AppBadge variant="info" size="sm">
            {creative.type}
          </AppBadge>
          {poolUrls.length > 1 && (
            <AppBadge variant="neutral" size="sm">
              {poolUrls.length} images
            </AppBadge>
          )}
          {cardCount > 0 && (
            <AppBadge variant="neutral" size="sm">
              {cardCount} {cardCount === 1 ? 'card' : 'cards'}
            </AppBadge>
          )}
          <AppBadge variant={CREATIVE_STATUS_VARIANT[creative.status] ?? 'neutral'} size="sm">
            {creative.status}
          </AppBadge>
        </div>
        {creative.headline && <p className={styles.creativeHeadline}>{creative.headline}</p>}
        {creative.description && (
          <p className={styles.creativeDescription}>{creative.description}</p>
        )}
        {creative.callToAction && (
          <AppBadge variant="default" size="sm" className={styles.creativeCta}>
            {creative.callToAction.replace(/_/g, ' ')}
          </AppBadge>
        )}
        <div className={styles.creativeActions}>
          <AppButton
            variant="secondary"
            size="sm"
            onClick={onEdit}
            disabled={!canEdit}
            title={
              canEdit
                ? 'Edit this creative'
                : 'Pause the campaign to edit its creatives — the ad platform rejects changes while ads are serving.'
            }
          >
            <Pencil size={14} />
            Edit
          </AppButton>
        </div>
      </div>
    </AppCard>
  );
}

/** Performance block on the Overview tab; loads independently of the campaign itself. */
function MetricsSection({ campaignId, currency }: { campaignId: string; currency: string }) {
  const { data: metrics, isLoading, isError } = useCampaignMetrics(campaignId);

  if (isLoading) {
    return (
      <div className={styles.tabLoading}>
        <div className={styles.skeleton} style={{ height: 120 }} />
        <div className={styles.skeleton} style={{ height: 300 }} />
      </div>
    );
  }

  if (isError || !metrics) {
    return <div className={styles.tabError}>Failed to load metrics.</div>;
  }

  return <CampaignPerformance metrics={metrics} currency={currency} />;
}

/**
 * Campaign-scoped slice of the notification feed. The API paginates org-wide with no
 * campaign filter, so match on the `campaignId` the backend puts in metadata and let
 * the user pull more pages when an older notification hasn't loaded yet.
 */
function NotificationsTab({ campaignId }: { campaignId: string }) {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications();
  const markReadMutation = useMarkNotificationRead();

  const campaignNotifications = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.data) ?? []).filter(
        (notification) => notification.metadata?.campaignId === campaignId,
      ),
    [data, campaignId],
  );

  const handleMarkRead = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) markReadMutation.mutate(notification.id);
    },
    [markReadMutation],
  );

  if (isLoading) {
    return (
      <div className={styles.tabLoading}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className={styles.skeleton} style={{ height: 72 }} />
        ))}
      </div>
    );
  }

  if (isError) {
    return <div className={styles.tabError}>Failed to load notifications.</div>;
  }

  return (
    <motion.div
      className={styles.notificationsContainer}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      {campaignNotifications.length === 0 ? (
        <div className={styles.tabPlaceholder}>
          <Bell size={32} />
          <p>No notifications for this campaign yet.</p>
        </div>
      ) : (
        <NotificationList notifications={campaignNotifications} onMarkRead={handleMarkRead} />
      )}

      {hasNextPage && (
        <div className={styles.loadMore}>
          <AppButton
            variant="secondary"
            size="sm"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          >
            Load more
          </AppButton>
        </div>
      )}
    </motion.div>
  );
}

export function CampaignDetailPage() {
  // `strict: false` so the component works whether mounted under the detail
  // route (`/promote/campaigns/$id`) or the edit route
  // (`/promote/campaigns/$id/edit`). The strict variant throws `Invariant
  // failed` in production when `from` doesn't match the active routeId.
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id ?? '';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const {
    data: campaign,
    isLoading,
    isError,
  } = useCampaign(id, {
    pollPlatformStatus: true,
  });
  const pauseMutation = usePauseCampaign();
  const resumeMutation = useResumeCampaign();
  const archiveMutation = useArchiveCampaign();
  const duplicateMutation = useDuplicateCampaign();
  const submitForReviewMutation = useSubmitForReview();
  const submitCampaignMutation = useSubmitCampaign();
  const syncStatusMutation = useSyncCampaignStatus();

  const handleSyncStatus = useCallback(() => {
    if (campaign?.platformCampaignId) syncStatusMutation.mutate(campaign.id);
  }, [campaign, syncStatusMutation]);

  const handleSubmitForReview = useCallback(() => {
    if (campaign) submitForReviewMutation.mutate(campaign.id);
  }, [campaign, submitForReviewMutation]);

  const handleLaunch = useCallback(() => {
    if (campaign) submitCampaignMutation.mutate(campaign.id);
  }, [campaign, submitCampaignMutation]);

  const handlePause = useCallback(() => {
    if (campaign) pauseMutation.mutate(campaign.id);
  }, [campaign, pauseMutation]);

  const handleResume = useCallback(() => {
    if (campaign) resumeMutation.mutate(campaign.id);
  }, [campaign, resumeMutation]);

  const handleArchive = useCallback(() => {
    if (!campaign) return;
    archiveMutation.mutate(campaign.id, {
      onSuccess: () => {
        setArchiveDialogOpen(false);
        navigate({ to: ROUTES.campaigns });
      },
    });
  }, [campaign, archiveMutation, navigate]);

  const handleDuplicate = useCallback(() => {
    if (!campaign) return;
    duplicateMutation.mutate(campaign.id, {
      onSuccess: (newCampaign) => {
        navigate({ to: '/promote/campaigns/$id', params: { id: newCampaign.id } });
      },
    });
  }, [campaign, duplicateMutation, navigate]);

  const handleEdit = useCallback(() => {
    if (!campaign) return;
    navigate({ to: '/promote/campaigns/$id/edit', params: { id: campaign.id } });
  }, [campaign, navigate]);

  if (isLoading) {
    return (
      <PageContainer
        title="Campaign"
        breadcrumbs={[{ label: 'Campaigns', href: ROUTES.campaigns }, { label: 'Campaign' }]}
      >
        <div className={styles.container}>
          <div className={styles.skeleton} style={{ height: 60 }} />
          <div className={styles.skeleton} style={{ height: 300 }} />
        </div>
      </PageContainer>
    );
  }

  if (isError || !campaign) {
    return (
      <PageContainer
        title="Campaign Not Found"
        breadcrumbs={[{ label: 'Campaigns', href: ROUTES.campaigns }, { label: 'Error' }]}
      >
        <div className={styles.errorState}>
          <p>This campaign could not be found or you do not have access.</p>
          <AppButton variant="secondary" onClick={() => navigate({ to: ROUTES.campaigns })}>
            Back to Campaigns
          </AppButton>
        </div>
      </PageContainer>
    );
  }

  const canPause = campaign.status === CampaignStatus.ACTIVE;
  const canResume = campaign.status === CampaignStatus.PAUSED;
  // Only DRAFT campaigns are editable — once a campaign is live on the ad
  // platform, Promote's form no longer represents the source of truth.
  const canEdit = campaign.status === CampaignStatus.DRAFT;
  // Surface a single primary launch action per management type so the user
  // isn't asked to pick between two similarly-labeled buttons.
  const canSubmitForReview =
    campaign.status === CampaignStatus.DRAFT && campaign.managementType === 'EVENTS_MANAGED';
  const canLaunch =
    campaign.status === CampaignStatus.DRAFT && campaign.managementType === 'SELF_MANAGED';

  const actionMenuItems = [
    ...(canPause
      ? [{ label: 'Pause Campaign', onClick: handlePause, icon: <Pause size={14} /> }]
      : []),
    ...(canResume
      ? [{ label: 'Resume Campaign', onClick: handleResume, icon: <Play size={14} /> }]
      : []),
    {
      label: 'Duplicate Campaign',
      onClick: handleDuplicate,
      icon: <Copy size={14} />,
    },
    // Demo-only entry to the isolated Stripe checkout; hidden unless the
    // build has a publishable key, so the launch flow is untouched.
    ...(checkoutEnabled
      ? [
          {
            label: 'Checkout (demo)',
            onClick: () =>
              navigate({ to: '/promote/campaigns/$id/checkout', params: { id: campaign.id } }),
            icon: <CreditCard size={14} />,
          },
        ]
      : []),
    {
      label: 'Archive Campaign',
      onClick: () => setArchiveDialogOpen(true),
      icon: <Archive size={14} />,
      destructive: true,
    },
  ];

  const tabs = [
    {
      value: 'overview',
      label: 'Overview',
      content: <OverviewTab campaign={campaign} />,
    },
    {
      value: 'creatives',
      label: 'Creatives',
      content: (
        <CreativesTab
          campaignId={campaign.id}
          platform={campaign.platform}
          status={campaign.status}
          creatives={campaign.creatives}
        />
      ),
    },
    {
      value: 'targeting',
      label: 'Targeting',
      content: (
        <TargetingTab targetingConfig={campaign.targetingConfig} platform={campaign.platform} />
      ),
    },
    {
      value: 'notifications',
      label: 'Notifications',
      content: <NotificationsTab campaignId={campaign.id} />,
    },
  ];

  return (
    <PageContainer
      title={campaign.name}
      breadcrumbs={[{ label: 'Campaigns', href: ROUTES.campaigns }, { label: campaign.name }]}
      actions={
        <div className={styles.headerActions}>
          {canLaunch && (
            <AppButton
              variant="primary"
              size="sm"
              onClick={handleLaunch}
              loading={submitCampaignMutation.isPending}
            >
              <Rocket size={14} />
              Launch
            </AppButton>
          )}
          {canSubmitForReview && (
            <AppButton
              variant="primary"
              size="sm"
              onClick={handleSubmitForReview}
              loading={submitForReviewMutation.isPending}
            >
              <Send size={14} />
              Submit for Review
            </AppButton>
          )}
          {canEdit && (
            <AppButton variant="secondary" size="sm" onClick={handleEdit}>
              <Pencil size={14} />
              Edit
            </AppButton>
          )}
          {canPause && (
            <AppButton
              variant="secondary"
              size="sm"
              onClick={handlePause}
              loading={pauseMutation.isPending}
            >
              <Pause size={14} />
              Pause
            </AppButton>
          )}
          {canResume && (
            <AppButton
              variant="secondary"
              size="sm"
              onClick={handleResume}
              loading={resumeMutation.isPending}
            >
              <Play size={14} />
              Resume
            </AppButton>
          )}
          {campaign.platformCampaignId && (
            <AppButton
              variant="ghost"
              size="sm"
              onClick={handleSyncStatus}
              loading={syncStatusMutation.isPending}
              aria-label="Sync status with ad platform"
              title="Fetch the latest status from the ad platform"
            >
              <RefreshCw size={14} />
              Sync
            </AppButton>
          )}
          <AppDropdownMenu
            trigger={
              <AppButton variant="ghost" size="sm" aria-label="More actions">
                <MoreHorizontal size={18} />
              </AppButton>
            }
            items={actionMenuItems}
          />
        </div>
      }
    >
      <motion.div
        className={styles.container}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className={styles.campaignHeader}>
          <CampaignStatusBadge
            status={campaign.status}
            effectiveStatus={campaign.effectiveStatus}
            lastSyncedAt={campaign.lastStatusSyncAt}
          />
          <PlatformTag platform={campaign.platform} size="lg" />
        </div>

        {campaign.status === CampaignStatus.REJECTED && (
          <div className={styles.rejectionBanner} role="alert">
            <AlertTriangle size={18} />
            <div className={styles.rejectionContent}>
              <strong>Campaign rejected by the ad platform.</strong>
              <p>
                {campaign.rejectionReason ??
                  'The ad platform did not approve this campaign. Edit the campaign to resolve the issue and submit it again.'}
              </p>
            </div>
            {canEdit && (
              <AppButton variant="secondary" size="sm" onClick={handleEdit}>
                Edit & resubmit
              </AppButton>
            )}
          </div>
        )}

        <AppTabs tabs={tabs} value={activeTab} onValueChange={setActiveTab} />
      </motion.div>

      <AppDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive Campaign"
        description="Are you sure you want to archive this campaign? This cannot be undone."
      >
        <div className={styles.dialogActions}>
          <AppButton variant="secondary" size="sm" onClick={() => setArchiveDialogOpen(false)}>
            Cancel
          </AppButton>
          <AppButton
            variant="danger"
            size="sm"
            onClick={handleArchive}
            loading={archiveMutation.isPending}
          >
            Archive
          </AppButton>
        </div>
      </AppDialog>
    </PageContainer>
  );
}
